// UC21-02 / UC21-03 — enrolling a Registrar. Archetype D with the RSUD lookup
// inline, drawn as the wireframe's "Registrar nou" (AdminRegistratori.tsx:510).
// Enrolment only: correcting the code and the type (UC21-06) is the dialog on
// the entity file since spec 052, and `registrars/:id/edit` redirects there.
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudTextInput, MudInfoBox, MudSelect } from '@egov-moldova/mud-react';
import { useToast } from '@simsm/ui-shared';
import { type ApiRefusal, apiClient, apiErrorKey, apiRefusal } from '../../app/api-client';
import { FormField, FormPage, FormRow, FormSection } from '../../components/FormPage/FormPage';
import { getRegistrar } from '../../features/registrars/api';
import { REGISTRAR_KINDS, registrarCodeOf } from '@simsm/shared-types';
import {
  IDNO_LENGTH,
  digitsOnly,
  hasCorrectionErrors,
  validateCorrection,
} from '../../features/registrars/correction';

interface RegistrarDto {
  id: string;
  idno: string;
  name: string;
  kind: string;
  mpassOrgCode: string;
}

interface LookupResponse {
  found: boolean;
  source: string;
  data?: { idno: string; name: string; statut: string };
}

// caiet UC21-02 — what the administrator enters: IDNO, cod, tip. The denumire
// is not part of it: RSUD supplies it (UC21-03) and only RSUD changes it (UC21-04).
// The code is the IDNO followed by 0000 (NC-UC10-01), so it is offered as soon as the
// IDNO is complete and sent as typed.
interface FormState {
  idno: string;
  kind: string;
}

interface CreatePayload extends FormState {
  mpassOrgCode: string;
}

// The answer RSUD gave, and for which IDNO. Keyed so that an answer arriving
// after the administrator changed the IDNO is never shown against the new one.
interface RsudAnswer {
  idno: string;
  name: string;
  errorKey: string | null;
}

function emptyForm(): FormState {
  return { idno: '', kind: '' };
}

export function RegistrarFormPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { role = 'system-admin' } = useParams<{ role: string }>();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [typedCode, setTypedCode] = useState<string | null>(null);
  const [rsud, setRsud] = useState<RsudAnswer | null>(null);
  const [submitError, setSubmitError] = useState<ApiRefusal | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const lookup = useMutation({
    mutationFn: (idno: string) =>
      apiClient.post<LookupResponse>('/admin/registrars/lookup', { idno }).then((r) => r.data),
    onSuccess: (resp, idno) =>
      setRsud(
        resp.found && resp.data
          ? { idno, name: resp.data.name, errorKey: null }
          : { idno, name: '', errorKey: 'registrars.modal.lookup_not_found' },
      ),
    // The code arrives on the NormalizedApiError the response interceptor
    // rejects with, so RSUD_UNAVAILABLE reads as an outage, not as "not found".
    onError: (err: unknown, idno) =>
      setRsud({
        idno,
        name: '',
        errorKey: apiErrorKey(err, 'registrars.modal.lookup_not_found'),
      }),
  });

  const create = useMutation({
    mutationFn: (payload: CreatePayload) =>
      apiClient.post<RegistrarDto>('/admin/registrars', payload).then((r) => r.data),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-registrars'] });
      // Said here, not on the page this navigates to: the toast host sits above
      // the router so the message outlives the navigation, and only this side
      // knows which operation ran.
      toast({ variant: 'success', title: t('registrars.toast.created'), text: created.name });
      // The file of the Registrar just enrolled, not the register it joined:
      // what the administrator does next — sites, credentials — starts there.
      navigate(`/${role}/registrars/${created.idno}`);
    },
    onError: (err: unknown) => setSubmitError(apiRefusal(err, 'registrars.error.create_failed')),
  });

  const answer = rsud?.idno === form.idno ? rsud : null;
  const name = answer?.name ?? '';
  const lookupPending = lookup.isPending && lookup.variables === form.idno;

  function changeIdno(value: string): void {
    const idno = digitsOnly(value).slice(0, IDNO_LENGTH);
    setForm({ ...form, idno });
    setTypedCode(null);
    if (idno.length === IDNO_LENGTH && rsud?.idno !== idno) lookup.mutate(idno);
  }

  const mpassOrgCode =
    typedCode ?? (form.idno.length === IDNO_LENGTH ? registrarCodeOf(form.idno) : '');
  const isDirty = JSON.stringify(form) !== JSON.stringify(emptyForm()) || typedCode !== null;
  const idnoInvalid = form.idno.length !== IDNO_LENGTH;
  // UC21-03: nothing is enrolled under a denumire RSUD did not supply, so a
  // create waits for RSUD's answer the way the wireframe does.
  const nameMissing = name === '';
  // UC21-02 makes the code and the type mandatory, UC21-05 makes the code
  // unique. The type is not cosmetic: `catalog.registrar.kind` is what admits
  // an IMSP to the cross-IMSP stock query (UC92) and bounds the observer's rows
  // (UC91-12). The rules are the correction dialog's, read from one place.
  const errors = validateCorrection({ ...form, mpassOrgCode });
  const canSave = !idnoInvalid && !nameMissing && !hasCorrectionErrors(errors);

  function handleSave(): void {
    setSubmitError(null);
    setSubmitted(true);
    if (!canSave) return;
    create.mutate({ ...form, mpassOrgCode });
  }

  const idnoHint = lookupPending
    ? t('registrars.modal.lookup_pending')
    : name
      ? t('registrars.modal.lookup_found')
      : null;

  return (
    <FormPage
      breadcrumb={[
        { label: t('nav.registrars'), href: `/${role}/registrars` },
        { label: t('registrars.new.title') },
      ]}
      title={t('registrars.new.title')}
      lede={t('registrars.new.lede')}
      saveLabel={t('registrars.action.save')}
      isSaving={create.isPending}
      isDirty={isDirty}
      refusal={submitError}
      canSave={canSave}
      onSave={handleSave}
      onCancel={() => navigate(`/${role}/registrars`)}
    >
      <FormSection title={t('registrars.section.identification')}>
        <FormField
          label={t('registrars.modal.field.idno')}
          fullWidth
          required
          {...(idnoHint ? { hint: idnoHint } : {})}
          error={submitted && idnoInvalid ? t('form.field.idno_len') : undefined}
        >
          <div className="simsm-registrars__idno-row">
            {/* Beside its lookup button, so FormField cannot hand it the label. */}
            <MudTextInput
              size="md"
              aria-label={t('registrars.modal.field.idno')}
              value={form.idno}
              inputMode="numeric"
              onMudInput={(e: CustomEvent<{ value: string }>) => changeIdno(e.detail.value)}
            />
            <MudButton
              shape="circular"
              variant="neutral"
              size="md"
              disabled={lookupPending || form.idno.length !== IDNO_LENGTH}
              onClick={() => lookup.mutate(form.idno)}
            >
              {lookupPending ? t('registrars.modal.lookup_pending') : t('registrars.modal.lookup')}
            </MudButton>
          </div>
          {answer?.errorKey ? (
            <MudInfoBox emphasis="strong" role="alert" variant="warning">
              {t(answer.errorKey)}
            </MudInfoBox>
          ) : null}
        </FormField>
        <FormField
          label={t('registrars.modal.field.name')}
          fullWidth
          hint={t('registrars.field.name_hint_new')}
        >
          <MudTextInput size="md" readonly value={name} />
        </FormField>
        <FormRow>
          <FormField
            label={t('registrars.modal.field.mpass_code')}
            required
            hint={t('registrars.modal.field.mpass_code_hint')}
            error={
              submitted && errors.mpassOrgCode
                ? t('registrars.field.mpass_code_invalid')
                : undefined
            }
          >
            <MudTextInput
              size="md"
              value={mpassOrgCode}
              inputMode="numeric"
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setTypedCode(digitsOnly(e.detail.value).slice(0, 17))
              }
            />
          </FormField>
          <FormField
            label={t('registrars.modal.field.kind')}
            required
            hint={t('registrars.modal.field.kind_hint')}
            error={submitted && errors.kind ? t('registrars.field.kind_required') : undefined}
          >
            <MudSelect
              size="medium"
              value={form.kind}
              aria-label={t('registrars.modal.field.kind')}
              placeholder={t('registrars.modal.field.kind_placeholder')}
              invalid={submitted && errors.kind !== undefined}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, kind: e.detail.value })
              }
            >
              {REGISTRAR_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`registrars.kind.${kind}`)}
                </option>
              ))}
            </MudSelect>
          </FormField>
        </FormRow>
      </FormSection>
    </FormPage>
  );
}

// `registrars/:id/edit` was the correction page until spec 052 moved the
// correction onto the file (owner ruling Q1, 2026-09-11). The route stays so a
// bookmark or an old link lands where the correction now is, rather than on a
// 404: the uuid resolves to the IDNO the file is addressed by.
export function RegistrarEditRedirect(): JSX.Element {
  const { t } = useTranslate();
  const { id = '', role = 'system-admin' } = useParams<{ id: string; role: string }>();
  const target = useQuery({
    queryKey: ['admin-registrar', id],
    queryFn: () => getRegistrar(id),
    retry: false,
  });

  if (target.data) {
    return <Navigate replace to={`/${role}/registrars/${target.data.idno}`} />;
  }
  if (target.isError) {
    return <Navigate replace to={`/${role}/registrars`} />;
  }
  return (
    <p className="simsm-query-state__loading" role="status" aria-live="polite">
      {t('common.loading')}
    </p>
  );
}
